import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PostCard } from './PostCard';
import { PostCardGrid } from './PostCardGrid';
import { PostCardSkeleton, PostCardGridSkeleton } from './PostCardSkeleton';
import { ProUpsellBanner } from './ProUpsellBanner';
import { PRO_ENABLED } from '@/lib/features';
import { NewsletterInlineCTA } from './NewsletterInlineCTA';
import { CommunityGarageCTA } from './CommunityGarageCTA';
import { trackFeedSessionEnd, categoriesToBreakdown } from '@/lib/analytics';
import { useUiStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { api, type Garage, type Post } from '@/lib/api';

interface FeedListProps {
  posts: Post[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
}

// SEO-379: Match a post to a garage by brand-name substring (case-insensitive),
// falling back to a category match. Within each tier the garage with the most
// members wins. Pure client-side; reuses the same /garages list TanStack Query
// already caches for the directory page so this is free for repeat visitors.
function pickMatchedGarage(post: Post, garageList: Garage[] | undefined) {
  if (!garageList || garageList.length === 0) return undefined;

  const brands = (post.brands ?? []).map((b) => b.toLowerCase()).filter(Boolean);
  const postCategory = post.category;

  let bestBrand: Garage | undefined;
  let bestCategory: Garage | undefined;
  for (const g of garageList) {
    const nameLower = g.name.toLowerCase();
    if (brands.length > 0 && brands.some((b) => nameLower.includes(b))) {
      if (!bestBrand || g.memberCount > bestBrand.memberCount) bestBrand = g;
    } else if (postCategory && g.category === postCategory) {
      if (!bestCategory || g.memberCount > bestCategory.memberCount) bestCategory = g;
    }
  }

  const best = bestBrand ?? bestCategory;
  if (!best) return undefined;
  return { slug: best.slug, name: best.name, emoji: best.emoji, memberCount: best.memberCount };
}

/** Returns true at position 5, then every 15 after that (5, 20, 35, ...) */
function isUpsellSlot(index: number): boolean {
  if (index === 4) return true; // 5th item (0-indexed)
  return index > 4 && (index - 4) % 15 === 0;
}

// UI-097: Community CTA every 15 items starting at position 14 (15th item).
// Offsets from pro-upsell (which starts at 5) to avoid stacking CTAs.
function isGarageCtaSlot(index: number): boolean {
  return index >= 14 && (index - 14) % 15 === 0;
}

// UI-032: Inject a newsletter capture once, after the 10th post in list view.
// One injection only — avoids re-firing impressions during scroll and keeps the
// feed scannable. Card-view variant lives in the grid render path below.
const NEWSLETTER_INLINE_SLOT = 9;

/** Maps feed index to a variant counter for rotating banner copy */
function upsellVariant(index: number): number {
  if (index === 4) return 0;
  return Math.floor((index - 4) / 15);
}

export function FeedList({ posts, isLoading, hasMore, onLoadMore, isLoadingMore }: FeedListProps) {
  const { viewMode } = useUiStore();
  const { user } = useAuthStore();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sessionStartRef = useRef<number>(Date.now());

  // SEO-379: Pull the public garage list once (shared cache key with /garages).
  // Stays in sync with that page; we only read from cache, never block the feed.
  const { data: garagesData } = useQuery({
    queryKey: ['garages', 'browse', '', ''],
    queryFn: () => api.garages.list({ limit: 30 }),
    staleTime: 5 * 60 * 1000,
  });
  const matchByPostId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof pickMatchedGarage>>();
    for (const p of posts) {
      const m = pickMatchedGarage(p, garagesData?.items);
      if (m) map.set(p.id, m);
    }
    return map;
  }, [posts, garagesData]);

  // ANALYTICS-010: Track feed session duration on unmount or tab blur
  useEffect(() => {
    const fireSession = () => {
      const breakdown = categoriesToBreakdown(posts.map((p) => p.category));
      trackFeedSessionEnd(posts.length, Date.now() - sessionStartRef.current, !hasMore, breakdown);
    };

    const onVisChange = () => {
      if (document.visibilityState === 'hidden') fireSession();
    };

    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      fireSession();
    };
  }, [posts.length, hasMore]);

  const lastPostRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          onLoadMore();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isLoadingMore, hasMore, onLoadMore]
  );

  if (isLoading) {
    return viewMode === 'cards' ? (
      <div className="grid grid-cols-1 gap-5 px-4 pt-6 pb-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <PostCardGridSkeleton key={i} />
        ))}
      </div>
    ) : (
      <div className="space-y-3 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <PostCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="rounded-2xl bg-bg-secondary border border-border-primary p-8 text-center max-w-md">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-bg-tertiary">
            <svg
              className="h-8 w-8 text-text-tertiary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-text-primary">No posts found</h3>
          <p className="mt-2 text-sm text-text-secondary">
            Try adjusting your filters or follow more sources to see content in your feed.
          </p>
        </div>
      </div>
    );
  }

  const showUpsell = !user?.isPro;

  if (viewMode === 'cards') {
    return (
      <div className="px-4 pt-6 pb-4">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
          {posts.map((post, index) => (
            <Fragment key={post.id}>
              <div ref={index === posts.length - 1 ? lastPostRef : undefined}>
                <PostCardGrid post={post} />
              </div>
              {index === NEWSLETTER_INLINE_SLOT && (
                <div className="sm:col-span-2 xl:col-span-3">
                  <NewsletterInlineCTA />
                </div>
              )}
              {isGarageCtaSlot(index) && (
                <div className="sm:col-span-2 xl:col-span-3">
                  <CommunityGarageCTA position={index} source="feed_grid" />
                </div>
              )}
            </Fragment>
          ))}
        </div>

        {showUpsell && posts.length >= 5 && (
          <div className="mt-5">
            {PRO_ENABLED && <ProUpsellBanner variantIndex={0} position="top" />}
          </div>
        )}

        {isLoadingMore && (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <PostCardGridSkeleton key={i} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      {posts.map((post, index) => (
        <div key={post.id}>
          <div ref={index === posts.length - 1 ? lastPostRef : undefined}>
            <PostCard post={post} matchedGarage={matchByPostId.get(post.id)} />
          </div>
          {index === NEWSLETTER_INLINE_SLOT && <NewsletterInlineCTA />}
          {PRO_ENABLED && showUpsell && isUpsellSlot(index) && <ProUpsellBanner variantIndex={upsellVariant(index)} position={index} />}
          {isGarageCtaSlot(index) && <CommunityGarageCTA position={index} source="feed_list" />}
        </div>
      ))}

      {isLoadingMore && (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <PostCardSkeleton key={i} />
          ))}
        </div>
      )}
    </div>
  );
}
